import { createTheme, alpha, Theme } from '@mui/material/styles';


// Extend MUI theme to add custom background keys and breakpoints
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true; // Custom breakpoint for ultra-wide screens
  }
  interface TypeBackground {
    white: string;
    black: string;
    sidebar: string;
    inputGrey: string;
    dialog: string;
    backdrop: string;
    scrollbar: string;
    green: string;
    lightGrey: string;
    red: string;
    darkGrey: string;
    primaryBlue: string;
    orange: string;
    shadowBlack: string;
    shadowBlack2: string;
    grey: string;
    hoverGrey: string;
    cardGradient: string;
    primaryGradient: string;
    accentGradient: string;
    purpleLight: string;
    purpleMedium: string;
    purpleDark: string;
    purpleVeryLight: string;
    purpleGradientLight: string;
    purpleGradientDark: string;
    whiteLight: string;
    whiteMedium: string;
    whiteDark: string;
    whiteVeryDark: string;
    whiteExtraDark: string;
    whiteUltraDark: string;
    greenLight: string;
    greenMedium: string;
    orangeLight: string;
    orangeMedium: string;
    orangeDark: string;
    blueLight: string;
    blueMedium: string;
    shadowBlackLight: string;
    shadowBlackMedium: string;
    blackLight: string;
    purpleHover: string;
    purplePressed: string;
    purpleSelectedBg: string;
    purpleSelectedText: string;
    whiteText85: string;
    whiteBgPressed: string;
    darkText75: string;
    darkBorder10: string;
    darkHover3: string;
    darkPressed6: string;
  }

  interface TypeText {
    dark: string;
    white: string;
    grey: string;
    light: string;
    purple: string;
    red: string;
    darkGrey: string;
    successGreen: string;
    warningOrange: string;
    primary: string;
    secondary: string;
  }
  interface TypeBorder {
    default: string;
    light: string;
    medium: string;
    purple: string;
    green: string;
    blueLight: string;
    blue: string;
  }

  interface Palette {
    border: TypeBorder;
  }

  interface PaletteOptions {
    border?: Partial<TypeBorder>;
  }

  // Extend Typography to add custom responsive font sizes and font weights
  interface Typography {
    fontSizes: {
      caption: { xs: string; sm: string; md: string; lg: string; xl: string };
      small: { xs: string; sm: string; md: string; lg: string; xl: string };
      body: { xs: string; sm: string; md: string; lg: string; xl: string };
      subheading: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading2: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading3: { xs: string; sm: string; md: string; lg: string; xl: string };
    };
    table: {
      body: { xs: string; sm: string; md: string; lg: string };
      small: { xs: string; sm: string; md: string; lg: string };
      header: { xs: string; sm: string; md: string; lg: string };
    };
    fontWeights: {
      regular: number;
      medium: number;
      semiBold: number;
      bold: number;
      extraBold: number;
      black: number;
    };
  }

  interface TypographyVariantsOptions {
    fontSizes?: {
      caption?: { xs: string; sm: string; md: string; lg: string; xl: string };
      small?: { xs: string; sm: string; md: string; lg: string; xl: string };
      body?: { xs: string; sm: string; md: string; lg: string; xl: string };
      subheading?: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading?: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading2?: { xs: string; sm: string; md: string; lg: string; xl: string };
      heading3?: { xs: string; sm: string; md: string; lg: string; xl: string };
    };
    table?: {
      body?: { xs: string; sm: string; md: string; lg: string };
      small?: { xs: string; sm: string; md: string; lg: string };
      header?: { xs: string; sm: string; md: string; lg: string };
    };
    fontWeights?: {
      regular?: number;
      medium?: number;
      semiBold?: number;
      bold?: number;
      extraBold?: number;
      black?: number;
    };
  }
}

// Shared configuration (breakpoints, typography)
const sharedConfig = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 2400, // Custom breakpoint for ultra-wide screens
    },
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizes: {
      caption: { xs: '0.585rem', sm: '0.63rem', md: '0.63rem', lg: '0.675rem', xl: '0.675rem' },
      small: { xs: '0.65rem', sm: '0.675rem', md: '0.675rem', lg: '0.75rem', xl: '0.75rem' },
      body: { xs: '0.72rem', sm: '0.765rem', md: '0.765rem', lg: '0.81rem', xl: '0.855rem' },
      subheading: { xs: '0.765rem', sm: '0.81rem', md: '0.81rem', lg: '0.855rem', xl: '0.9rem' },
      heading: { xs: '0.9rem', sm: '0.945rem', md: '0.945rem', lg: '0.99rem', xl: '1.035rem' },
      heading2: { xs: '1.035rem', sm: '1.07rem', md: '1.105rem', lg: '1.295rem', xl: '1.385rem' },
      heading3: { xs: '1.17rem', sm: '1.205rem', md: '1.24rem', lg: '1.43rem', xl: '1.52rem' },
    },
    table: {
      small: { xs: '0.63rem', sm: '0.675rem', md: '0.675rem', lg: '0.72rem' },
      body: { xs: '0.72rem', sm: '0.765rem', md: '0.765rem', lg: '0.81rem' },
      header: { xs: '0.74rem', sm: '0.785rem', md: '0.795rem', lg: '0.8325rem' },
    },
    fontWeights: {
      regular: 400,    // Normal text, default body text
      medium: 500,    // Secondary emphasis, labels, metadata
      semiBold: 600,  // Primary emphasis, headings, buttons, table headers
      bold: 700,      // Strong emphasis, section titles
      extraBold: 800, // Large numbers, statistics, hero text
      black: 900,     // Maximum emphasis (use sparingly)
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: '12px' },
      },
    },
  

    // ✅ Global input styles (TextField / OutlinedInput)
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.text.secondary,
          fontSize: {
            xs: '0.72rem',
            sm: '0.765rem',
            md: '0.765rem',
            lg: '0.81rem',
            xl: '0.855rem',
          } as any,
          '&.Mui-focused': {
            color: theme.palette.text.secondary,
          },
        }),
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          width: '100%',
          borderRadius: 8, // same as your sx borderRadius: 2
          backgroundColor: theme.palette.background.inputGrey,
          minHeight: 44,

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.light,
            borderWidth: 1,
          },

          // hover (subtle)
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.medium,
          },

          // focused (purple)
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.purple,
            borderWidth: 1,
          },

          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.purple,
          },
        }),
        MuiAutocomplete: {
          styleOverrides: {
            inputRoot: {
              paddingRight: '36px', // space for clear + dropdown icons
            },
          },
        },

        input: () => ({
          padding: '12px 14px',
          fontSize: {
            xs: '0.72rem',
            sm: '0.765rem',
            md: '0.765rem',
            lg: '0.81rem',
            xl: '0.855rem',
          } as any,
          '&[type=number]': {
            MozAppearance: 'textfield',
          },
          '&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }),
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.border.purple, // Use theme purple
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: theme.palette.text.purple || theme.palette.border.purple, // Use theme purple
            '&.Mui-checked': {
              backgroundColor: theme.palette.primary.main, // Use primary color when checked
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: alpha(theme.palette.border.purple, 0.3), // Use theme purple with opacity
            opacity: 1,
          },
        }),
      },
    },
  }
};

// Light theme palette
const lightPalette = {
  mode: 'light' as const,
  background: {
    white: '#ffffff',
    black: '#000000',
    sidebar: '#f8f8fa',
    inputGrey: alpha('#ffffff', 0.06),
    dialog: '#ffffff',
    backdrop: 'rgba(0, 0, 0, 0.5)',
    scrollbar: '#e0e0e0',
    green: '#2e7d32',
    lightGrey: '#f8f9fa',
    red: '#dc2626',
    darkGrey: '#212121',
    primaryBlue: '#1976d2',
    orange: '#f59e0b',
    shadowBlack: '#0f172a',
    shadowBlack2: '#1e293b',
    grey: '#f1f5f9',
    hoverGrey: '#e2e8f0',
    cardGradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    accentGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    purple: '#8B5CF6',
    purpleLight: 'rgba(139, 92, 246, 0.05)',
    purpleMedium: 'rgba(139, 92, 246, 0.1)',
    purpleDark: 'rgba(139, 92, 246, 0.15)',
    purpleVeryLight: 'rgba(139, 92, 246, 0.08)',
    purpleGradientLight: 'rgba(139, 92, 246, 0.7)',
    purpleGradientDark: 'rgba(139, 92, 246, 0.8)',
    whiteLight: 'rgba(255, 255, 255, 0.1)',
    whiteMedium: 'rgba(255, 255, 255, 0.15)',
    whiteDark: 'rgba(255, 255, 255, 0.2)',
    whiteVeryDark: 'rgba(255, 255, 255, 0.25)',
    whiteExtraDark: 'rgba(255, 255, 255, 0.4)',
    whiteUltraDark: 'rgba(255, 255, 255, 0.9)',
    greenLight: 'rgba(22, 163, 74, 0.1)',
    greenMedium: 'rgba(22, 163, 74, 0.15)',
    orangeLight: 'rgba(245, 158, 11, 0.1)',
    orangeMedium: 'rgba(245, 158, 11, 0.15)',
    orangeDark: 'rgba(245, 158, 11, 0.2)',
    blueLight: 'rgba(25, 118, 210, 0.1)',
    blueMedium: 'rgba(25, 118, 210, 0.2)',
    shadowBlackLight: 'rgba(26, 26, 26, 0.1)',
    shadowBlackMedium: 'rgba(26, 26, 26, 0.2)',
    blackLight: 'rgba(0, 0, 0, 0.2)',
  },
  text: {
    dark: '#1a1a1a',
    white: '#ffffff',
    grey: '#666666',
    light: '#9E9E9E',
    purple: '#8B5CF6',
    red: '#dc2626',
    darkGrey: '#212121',
    successGreen: '#4CAF50',
    warningOrange: '#f59e0b',
    primary: '#0f172a',
    secondary: '#475569',
  },
  border: {
    default: '#e0e0e0',
    light: '#d1d5db',
    medium: '#9ca3af',
    purple: '#8B5CF6',
    green: '#16a34a',
    blueLight: '#e3f2fd',
    blue: '#1976d2',
    purpleLight: 'rgba(139, 92, 246, 0.2)',
    purpleMedium: 'rgba(139, 92, 246, 0.3)',
    whiteLight: 'rgba(255, 255, 255, 0.2)',
    whiteMedium: 'rgba(255, 255, 255, 0.3)',
  },
  primary: {
    main: '#667eea',
    light: '#8b9bef',
    dark: '#5568d3',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#764ba2',
    light: '#9168b8',
    dark: '#5f3c85',
    contrastText: '#ffffff',
  },
  success: {
    main: '#10b981',
    light: '#34d399',
    dark: '#059669',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
  },
};

// Dark theme palette - inverting but keeping black/white/light purple consistent
const darkPalette = {
  mode: 'dark' as const,
  background: {
    white: '#282828',
    black: '#000000',
    sidebar: '#1D1D1D',
    inputGrey: alpha('#ffffff', 0.06),
    dialog: '#1a1a1a',
    backdrop: 'rgba(0, 0, 0, 0.8)',
    scrollbar: '#3a3a3a',
    green: '#2e7d32',
    lightGrey: '#252525',
    red: '#ff6b6b',
    darkGrey: '#3a3a3a',
    primaryBlue: '#42a5f5',
    orange: '#f59e0b',
    purpleLight: 'rgba(139, 92, 246, 0.05)',
    purpleMedium: 'rgba(139, 92, 246, 0.1)',
    purpleDark: 'rgba(139, 92, 246, 0.15)',
    purpleVeryLight: 'rgba(139, 92, 246, 0.08)',
    whiteLight: 'rgba(255, 255, 255, 0.1)',
    whiteMedium: 'rgba(255, 255, 255, 0.15)',
    whiteDark: 'rgba(255, 255, 255, 0.2)',
    whiteVeryDark: 'rgba(255, 255, 255, 0.25)',
    whiteExtraDark: 'rgba(255, 255, 255, 0.4)',
    whiteUltraDark: 'rgba(255, 255, 255, 0.9)',
    greenLight: 'rgba(22, 163, 74, 0.1)',
    greenMedium: 'rgba(22, 163, 74, 0.15)',
    orangeLight: 'rgba(245, 158, 11, 0.1)',
    orangeMedium: 'rgba(245, 158, 11, 0.15)',
    orangeDark: 'rgba(245, 158, 11, 0.2)',
    blueLight: 'rgba(66, 165, 245, 0.1)',
    blueMedium: 'rgba(66, 165, 245, 0.2)',
    shadowBlackLight: 'rgba(26, 26, 26, 0.1)',
    shadowBlackMedium: 'rgba(26, 26, 26, 0.2)',
    blackLight: 'rgba(0, 0, 0, 0.2)',
    purpleHover: '#7C3AED',
    purplePressed: '#6D28D9',
    purpleSelectedBg: 'rgba(139, 92, 246, 0.18)',
    purpleSelectedText: '#E9DDFF',
    whiteText85: 'rgba(255, 255, 255, 0.85)',
    whiteBgPressed: 'rgba(255, 255, 255, 0.12)',
    darkText75: 'rgba(17, 24, 39, 0.75)',
    darkBorder10: 'rgba(17, 24, 39, 0.10)',
    darkHover3: 'rgba(17, 24, 39, 0.03)',
    darkPressed6: 'rgba(17, 24, 39, 0.06)',
  },
  text: {
    dark: '#ffffff',
    white: '#ffffff',
    grey: '#b0b0b0',
    light: '#808080',
    purple: '#8B5CF6',
    red: '#ff6b6b',
    darkGrey: '#e0e0e0',
    successGreen: '#4CAF50',
    warningOrange: '#f59e0b',
  },
  border: {
    default: '#3a3a3a',
    light: '#2d2d2d',
    medium: '#4a4a4a',
    purple: '#8B5CF6',
    green: '#16a34a',
    blueLight: '#1a3a5c',
    blue: '#42a5f5',
    purpleLight: 'rgba(139, 92, 246, 0.2)',
    purpleMedium: 'rgba(139, 92, 246, 0.3)',
    whiteLight: 'rgba(255, 255, 255, 0.2)',
    whiteMedium: 'rgba(255, 255, 255, 0.3)',
  },
  primary: {
    main: '#8b9bef',
    light: '#a5b4f5',
    dark: '#667eea',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#9168b8',
    light: '#a67fc7',
    dark: '#764ba2',
    contrastText: '#ffffff',
  },
  success: {
    main: '#34d399',
    light: '#6ee7b7',
    dark: '#10b981',
  },
  error: {
    main: '#f87171',
    light: '#fca5a5',
    dark: '#ef4444',
  },
  warning: {
    main: '#fbbf24',
    light: '#fcd34d',
    dark: '#f59e0b',
  },
  info: {
    main: '#60a5fa',
    light: '#93c5fd',
    dark: '#3b82f6',
  },
};

// Create light theme
export const createLightTheme = () => createTheme({
  ...sharedConfig,
  palette: lightPalette,
});

// Create dark theme
export const createDarkTheme = () => createTheme({
  ...sharedConfig,
  palette: darkPalette,
});

// Export default light theme for backward compatibility
export const theme = createLightTheme();

// Export theme type for TypeScript support
export type AppTheme = ReturnType<typeof createLightTheme>;

